import { useState, useCallback, useEffect } from "react";
import { useFormik } from "formik";
import { getSetting36Agreement, postSetting36Agreement, getSetting36AgreementPrevious } from "api/master36Agreement";
import useToastNotification from "hooks/useToastNotification";
import AttendSetting36AgreementDomain, {AttendSetting36Agreement} from 'domain/master/attend/attendSetting36Agreement';

const { errorNotification, successNotification } = useToastNotification();

export const useMasterSettingAgreement36Form = () => {

  const [isLoading, setIsLoading] = useState(false);
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const listMonths = [
    '01', '02', '03', '04', '05', '06', '07', '08', '09',
    '10', '11', '12'
  ];

  const monthOptions = listMonths.map((month) => ({
    value: month,
    label: month,
  }));
  
  const checkSorted = (arr: Array<number>) => {
    for(let i = 0; i < arr.length - 1; i++) {
      if(arr[i] >= arr[i+1]) {
         return false;
      }
    }
    return true;
  }
  
  const checkValidateArrThreshold = (arrThreshold: Array<number>, minValue: number, maxValue: number) => {
    let inRange = Math.max(...arrThreshold) <= maxValue && Math.min(...arrThreshold) >= minValue;
    let isSorted = checkSorted(arrThreshold);
    if(inRange && isSorted){
      return true;
    }
    return false
  }

  // submit formik
  const onSubmit = async (values: AttendSetting36AgreementDomain) => {
    

    setOpenModal(false);
    let postData = values.getPostData();
    if(!checkSorted(postData.monthlyOvertimeHourList)){
      errorNotification('当月の時間外労働時間の値が不正です。上の設定値よりも大きい値を入力してください');
      return;
    }
    if(!checkSorted(postData.yearOvertimeHourList)){
      errorNotification('当年の時間外労働時間の値が不正です。上の設定値よりも大きい値を入力してください');
      return;
    }
    if(!checkSorted(postData.monthlyOverConditionsCountList)){
      errorNotification('時間外労働届出の上限の値が不正です。上の設定値よりも大きい値を入力してください');
      return;
    }
    try {
      const response = await postSetting36Agreement(values.getPostData());
      successNotification(isEdit ? '更新しました。' : '登録しました。')
    } catch (error) {
      setOpenModal(false);
      errorNotification(isEdit ? '更新しませんでした。' : '登録しませんでした。')
      if (error.response && error.response.data && error.response.data.errors) {
        const listErr = error.response.data.errors;
        let stringErr = '';
        listErr.map((element: any) => {
          stringErr += `${element.defaultMessage} \n`;
          return stringErr;
        });
        errorNotification(stringErr);
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
      }
    }
  }

  const formik = useFormik({
    initialValues: AttendSetting36AgreementDomain.generateInitial(),
    onSubmit,
  });
  
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      await getSetting36Agreement(targetYear).then((response: any) => {
        let tmpDetail = new AttendSetting36AgreementDomain(response);
        setIsEdit(tmpDetail.agreementsId !== null);
        if(!tmpDetail.targetYear){
          tmpDetail.targetYear = targetYear;
        }
        if(!tmpDetail.applyStartMonth){
          tmpDetail.applyStartMonth = new Date(targetYear, 3, 1);          
        }else{
          tmpDetail.applyStartMonth = new Date(tmpDetail.applyStartMonth);          
        }
        formik.setValues(tmpDetail);
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      errorNotification('サーバー側でエラーが発生しました。');
    }
  }, [targetYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData, targetYear])

  const setPreviousYearData = async () => {
    try {
      setIsLoading(true);
      await getSetting36AgreementPrevious(targetYear).then((response: any) => {
        let tmpDetail = new AttendSetting36AgreementDomain(response);
        setIsEdit(tmpDetail.agreementsId !== null);
        if(!tmpDetail.applyStartMonth){
          tmpDetail.applyStartMonth = new Date(targetYear, 3, 1);          
        }else{
          tmpDetail.applyStartMonth = new Date(tmpDetail.applyStartMonth);          
        }
        formik.setValues(tmpDetail);
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.data && error.response.data.defaultMessage) {
        errorNotification(error.response.data.defaultMessage);
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
        throw error;
      }
    }
  }

  return {
    isLoading,
    targetYear,
    setTargetYear,
    listMonths,
    monthOptions,
    formik,
    openModal,
    setOpenModal,
    isEdit,
    setPreviousYearData
  };
};

export default useMasterSettingAgreement36Form;
